import { EntityStatus, PersonStatus } from '../gql/graphql';
import { getPaginatedNodes, toPagination } from './utils';

import { convergeLogger } from '@converge/config';
import { graphql } from '../gql';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

export const GET_ENTITIES_BROKER_AGENCIES = graphql(`
  query getBrokerAgencies(
    $pagination: PaginationRequest
    $filters: EntityFilterInput!
    $sort: PaginationSort
  ) {
    entities(pagination: $pagination, filters: $filters, sort: $sort) {
      edges {
        cursor
        node {
          uuid
          referenceId
          type
          status
          fein
          agency
          brokerCommission
          digitalCommission
          retailCommission
          insurer
          status
          createdAt
          aliases(
            filters: { source: PRIMARY, type: LEGAL_NAME }
            pagination: { first: 1 }
          ) {
            edges {
              cursor
              queryCursor
              node {
                uuid
                name
                type
                sources
                primary
              }
            }
          }
        }
      }
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
        pageNumber
        pageSize
      }
    }
  }
`);

export function useGetEntitiesBrokerAgencies({
  pageSize = 1000,
  uuid,
  isAgency = true,
  brokerageName = '',
}: {
  pageSize?: number;
  uuid?: string;
  isAgency?: boolean;
  brokerageName?: string;
}) {
  const { loading, error, data } = useQuery(GET_ENTITIES_BROKER_AGENCIES, {
    variables: {
      pagination: toPagination(0, pageSize),
      filters: {
        agency: isAgency,
        entityUuid: uuid,
        status: EntityStatus.Active,
        brokerageName: brokerageName,
      },
    },
    fetchPolicy: 'network-only',
  });

  const brokerAgencies: BrokerAgency[] = useMemo(() => {
    if (!data || !data.entities || !data.entities.edges) return [];

    return getPaginatedNodes(data.entities)
      .filter((e) => e.agency && e.status === 'ACTIVE')
      .map((edge) => {
        const alias = getPaginatedNodes(edge.aliases);
        if (!alias) {
          convergeLogger.error(
            `No primary legal name alias found for broker ${edge.uuid}`
          );
        }
        return {
          value: edge.uuid,
          label: alias[0]?.name ?? edge.referenceId ?? 'N/A',
          brokerage: alias[0]?.name ?? edge.referenceId ?? 'N/A',
          brokerCommission: edge.brokerCommission ?? 0,
        };
      });
  }, [data]);

  return {
    error,
    loading,
    data,
    brokerAgencies,
  };
}

export interface BrokerAgency {
  value: string;
  label: string;
  brokerage: string;
  brokerCommission: number;
}

export interface IBrokerAgent {
  status?: PersonStatus | null;
  value: string;
  label: string;
  email: string;
  phone: string;
  uuid: string;
  firstName: string;
  lastName: string;
}
